import { DatesRangeValue } from '@mantine/dates';
import { useCallback } from 'react';
import { FEEDBACK_RATING_DOWN } from 'src/app/chat/messageFeedback';
import {
  FeedbackRating,
  readFeedbackFilters,
  writeFeedbackFilters,
} from 'src/routes/Settings/AIAssistant/aiAssistantSearchParams';
import { useSearchParamsState } from 'src/hooks/useSearchParamsState';
import { useDefaultDateRange } from 'src/routes/Settings/AIAssistant/hooks/useDefaultDateRange';

export interface IUseFeedbackFiltersResult {
  dateRange: DatesRangeValue;
  rating: FeedbackRating | undefined;
  reasons: string[];
  userId: number | undefined;
  setDateRange: (value: DatesRangeValue) => void;
  /** Clicking the active rating clears it; leaving "down" clears the reasons. */
  toggleRating: (value: FeedbackRating) => void;
  /** Adding a reason also selects the "down" rating. */
  toggleReason: (reason: string) => void;
  setUserId: (value: number | undefined) => void;
}

/**
 * Feedback-tab filters backed by the URL search params, so Overview links can
 * deep-link a filtered view. A URL without dates falls back to the same
 * default range the other admin tabs use.
 */
export const useFeedbackFilters = (): IUseFeedbackFiltersResult => {
  const [defaultRange] = useDefaultDateRange();
  const [filters, update] = useSearchParamsState(
    readFeedbackFilters,
    writeFeedbackFilters,
  );

  const setDateRange = useCallback(
    (dateRange: DatesRangeValue) => update(() => ({ dateRange })),
    [update],
  );

  const toggleRating = useCallback(
    (value: FeedbackRating) =>
      update((current) =>
        current.rating === value
          ? { rating: undefined, reasons: [] }
          : { rating: value },
      ),
    [update],
  );

  const toggleReason = useCallback(
    (reason: string) =>
      update((current) =>
        current.reasons.includes(reason)
          ? { reasons: current.reasons.filter((r) => r !== reason) }
          : {
              rating: FEEDBACK_RATING_DOWN,
              reasons: [...current.reasons, reason],
            },
      ),
    [update],
  );

  const setUserId = useCallback(
    (userId: number | undefined) => update(() => ({ userId })),
    [update],
  );

  return {
    dateRange: filters.dateRange ?? defaultRange,
    rating: filters.rating,
    reasons: filters.reasons,
    userId: filters.userId,
    setDateRange,
    toggleRating,
    toggleReason,
    setUserId,
  };
};
